import { fetchMemberOfTeams } from "../../api/api";
import { useState } from "react";
import Input from "../../components/Input";
import Button from "../../components/Button";
import Card from "../../components/Card";
import Stack from "../../components/Stack";
import Header from "../../components/Header";
import KebabMenu from "../../components/KebabMenu";
import { useNavigate } from "react-router-dom";
import { deleteCompetitionTeam } from "../../api/api";

export default function RepCompetitionTeams() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [badgeId, setBadgeId] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  const getRepTeams = () => {
    fetchMemberOfTeams(badgeId)
      .then(data => setTeams(data))
      .catch(error => console.error('Error fetching employee teams:', error));
  }

  const toggleExpanded = (teamId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(teamId) ? next.delete(teamId) : next.add(teamId);
      return next;
    });
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try {
      await deleteCompetitionTeam(teamId);
      setExpanded((prev) => {
        const n = new Set(prev);
        n.delete(teamId);
        return n;
      });
      getRepTeams();
    } catch (err) {
      console.error(err);
      alert('Failed to delete team');
    }
  };

  return (
    <Card>
      <Stack gap="5">
        <Header>Rep Search</Header>
        <p className="text-xs">Enter badge ID of a rep to find all of the teams a rep is associated with.</p>
        <Input value={badgeId} onChange={e => setBadgeId(e.target.value)} placeholder="Enter Badge ID" type="number"/>
        <Button onClickMethod={getRepTeams}>Search Teams</Button>
        {teams.length > 0 && (
          <Stack align="start" gap="3" className="w-full">
            <Header size="md">Results:</Header>
          </Stack>
        )}
        <ul className="w-full flex flex-col items-center">
          {teams.map((team) => {
            const memberCount = team.competition_team_members?.length || 0;
            const isExpanded = expanded.has(team.id);

            const division = team.division || team.division_name || 'N/A';
            const office = team.office || team.office_name || 'N/A';
            const address = team.address || 'N/A';
            const creator = team.team_creator;

            return (
              <div key={team.id} className="w-full rounded-md border border-gray-200 bg-white shadow-sm">
                {/* Card Header Row */}
                <button
                  type="button"
                  onClick={() => toggleExpanded(team.id)}
                  className="w-full text-left p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold">{team.name}</h3>
                        </div>

                        {/* Meta lines */}
                        <div className="mt-1 space-y-0.5 text-xs text-gray-700">
                          <div>
                            <strong>Division:</strong> {division}
                          </div>
                          <div>
                            <strong>Office:</strong> {office}
                          </div>
                          <div>
                            <strong>Address:</strong> {address}
                          </div>
                          <div className="flex gap-1">
                            <strong>Creator:</strong>
                            {creator && <p>{creator.name}</p>}
                          </div>
                          <div>
                            <strong>Team Members:</strong> {memberCount}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          team.is_active ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {team.is_active ? 'Active' : 'Inactive'}
                      </span>

                      <KebabMenu
                        onEdit={() =>
                          navigate(
                            `/admin/competitions/${team.competition_id}/competition_events/${team.competition_event_id}/competition_team/${true}/${team.id}`
                          )
                        }
                        onDelete={() => handleDeleteTeam(team.id)}
                      />
                    </div>
                  </div>
                </button>

                {/* Expanded body */}
                {isExpanded && (
                  <div className="px-6 pb-4">
                    <div className="border-t border-gray-200 pt-3">
                      <h4 className="text-sm font-semibold mb-2">Team Members</h4>
                      {memberCount === 0 ? (
                        <p className="text-sm text-gray-500">No members yet.</p>
                      ) : (
                        <ul className="list-disc pl-5 space-y-1 text-sm">
                          {team.competition_team_members.map((m) => {
                            const name = m.name || '';
                            const role = m.role || '';
                            const officeName = m.office || m.office_name || '';
                            return (
                              <li key={m.id}>
                                <span className="font-medium">{name}</span>
                                {role && ` (${role})`}
                                {officeName && ` — ${officeName}`}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </ul>
      </Stack>
    </Card>
  );
}
